/**
 * ProgramCard — shared aspect. Light-or-dark surface card for the
 * "My Programs" grid on the student home view (and any future
 * surface that wants the same card shape).
 *
 * Composition: type-meta eyebrow + status chip header row, Cormorant
 * title, optional sponsor line, phase progress (n/total + name italic
 * + segmented bar), footer with days-left italic + closes date + View
 * link.
 *
 * Consumer pre-computes typeMeta / daysLeft / closesDateShort — the
 * aspect stays presentational and locale-agnostic.
 */
import {
  Platform,
  Pressable,
  Text,
  View,
  type ViewStyle,
} from "react-native";
import Svg, { Path } from "react-native-svg";

import { COLORS, FONTS, RADII, SPACING, STROKES } from "../../lib/brand";
import type { PaneTheme } from "../brand/paneThemes";
import { ProgressBar } from "./ProgressBar";

export type ProgramCardStatus = "active" | "judging";
export type ProgramCardSurface = "light" | "dark";

export type ProgramCardPhase = {
  /** 1-based index of the active segment. */
  index: number;
  /** Total number of segments in the bar. Typically 5. */
  total: number;
  /** Right-aligned italic phase name. */
  name: string;
};

export type ProgramCardProps = {
  theme: PaneTheme;
  status: ProgramCardStatus;
  surface?: ProgramCardSurface;
  typeMeta: string;
  title: string;
  sponsorName?: string;
  phase: ProgramCardPhase;
  daysLeft?: number | null;
  closesDateShort?: string | null;
  /** Default "View". */
  viewLabel?: string;
  onPress: () => void;
  accessibilityLabel?: string;
};

export function ProgramCard({
  theme,
  status,
  surface = "light",
  typeMeta,
  title,
  sponsorName,
  phase,
  daysLeft,
  closesDateShort,
  viewLabel = "View",
  onPress,
  accessibilityLabel,
}: ProgramCardProps) {
  const isWeb = Platform.OS === "web";
  const isDark = surface === "dark";

  const isJudging = status === "judging";
  const statusLabel = isJudging ? "In review" : "Registered";
  const statusColor = isJudging ? COLORS.AMBER.hex : COLORS.SAGE.hex;
  const statusBg = isJudging
    ? theme.accentTintSoft
    : theme.successTintSoft;
  const statusBorder = isJudging
    ? theme.accentBorderSoft
    : theme.successBorderSoft;

  const cardShadow = isDark
    ? "0 2px 8px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.10)"
    : "0 1px 2px rgba(28,43,26,0.04), 0 4px 14px rgba(28,43,26,0.05)";
  const cardHoverShadow = isDark
    ? "0 14px 36px -12px rgba(0,0,0,0.45), 0 2px 6px rgba(0,0,0,0.20)"
    : "0 12px 32px -16px rgba(28,43,26,0.20), 0 2px 6px rgba(28,43,26,0.06)";

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      style={({ hovered, pressed }: { hovered?: boolean; pressed?: boolean }) => [
        {
          backgroundColor: theme.surfaceBg,
          borderRadius: RADII.md,
          borderWidth: STROKES.hairline,
          borderColor: theme.border,
          paddingHorizontal: 22,
          paddingVertical: 20,
          overflow: "hidden",
        },
        isWeb
          ? ({
              cursor: "pointer",
              transition:
                "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
              boxShadow: cardShadow,
            } as unknown as ViewStyle)
          : null,
        isWeb && hovered
          ? ({
              transform: [{ translateY: -1 }],
              boxShadow: cardHoverShadow,
              borderColor: theme.accentBorderHover,
            } as unknown as ViewStyle)
          : null,
        pressed ? { opacity: 0.96 } : null,
      ]}
    >
      {/* Header row */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <Text
          numberOfLines={1}
          style={{
            flex: 1,
            minWidth: 0,
            fontFamily: theme.bodyBoldFont,
            fontSize: 9.5,
            letterSpacing: 1.8,
            textTransform: "uppercase",
            color: theme.accent,
          }}
        >
          {typeMeta}
        </Text>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
            paddingHorizontal: SPACING.xs,
            paddingVertical: 3,
            borderRadius: RADII.full,
            borderWidth: STROKES.hairline,
            backgroundColor: statusBg,
            borderColor: statusBorder,
          }}
        >
          <View
            style={{
              width: 6,
              height: 6,
              borderRadius: RADII.full,
              backgroundColor: statusColor,
            }}
          />
          <Text
            style={{
              fontFamily: theme.bodyBoldFont,
              fontSize: 9,
              letterSpacing: 1.6,
              color: statusColor,
              textTransform: "uppercase",
            }}
          >
            {statusLabel}
          </Text>
        </View>
      </View>

      {/* Title */}
      <Text
        numberOfLines={2}
        style={{
          marginTop: 14,
          fontFamily: theme.displayMediumFont,
          fontSize: 22,
          lineHeight: 28,
          letterSpacing: -0.12,
          color: theme.textOnSurface,
        }}
      >
        {title}
      </Text>

      {/* Sponsor */}
      {sponsorName && sponsorName.length > 0 ? (
        <Text
          numberOfLines={1}
          style={{
            marginTop: 12,
            fontFamily: theme.bodyFont,
            fontSize: 12,
            lineHeight: 18,
            color: theme.textOnSurfaceDim,
          }}
        >
          Sponsored by{" "}
          <Text style={{ fontFamily: theme.bodyBoldFont, color: theme.textOnSurface }}>
            {sponsorName}
          </Text>
        </Text>
      ) : null}

      {/* Phase progress */}
      <View style={{ marginTop: 20 }}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <Text
            style={{
              fontFamily: theme.bodyBoldFont,
              fontSize: 10,
              letterSpacing: 1.8,
              textTransform: "uppercase",
              color: theme.textOnSurfaceDim,
            }}
          >
            {`Phase ${phase.index}/${phase.total}`}
          </Text>
          <Text
            numberOfLines={1}
            style={{
              flexShrink: 1,
              minWidth: 0,
              textAlign: "right",
              fontFamily: FONTS.displayItalic,
              fontSize: 12,
              color: theme.textOnSurfaceDim,
            }}
          >
            {phase.name}
          </Text>
        </View>

        <View style={{ marginTop: 6 }}>
          <ProgressBar theme={theme} segments={phase.total} current={phase.index} />
        </View>
      </View>

      {/* Footer */}
      <View
        style={{
          marginTop: 18,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <View
          style={{
            flex: 1,
            minWidth: 0,
            flexDirection: "row",
            alignItems: "baseline",
          }}
        >
          {daysLeft != null && daysLeft > 0 ? (
            <Text
              style={{
                fontFamily: FONTS.displayItalic,
                fontSize: 14,
                color: theme.accent,
                marginRight: 8,
              }}
            >
              {`${daysLeft} day${daysLeft === 1 ? "" : "s"} left`}
            </Text>
          ) : null}
          {closesDateShort ? (
            <Text
              numberOfLines={1}
              style={{
                flexShrink: 1,
                minWidth: 0,
                fontFamily: theme.bodyFont,
                fontSize: 11,
                color: theme.textOnSurfaceDim,
              }}
            >
              {(daysLeft != null && daysLeft > 0 ? "· " : "") +
                "Closes " +
                closesDateShort}
            </Text>
          ) : null}
        </View>

        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <Text
            style={{
              fontFamily: theme.bodyBoldFont,
              fontSize: 10.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: theme.accent,
            }}
          >
            {viewLabel}
          </Text>
          <Svg width={12} height={12} viewBox="0 0 12 12" fill="none">
            <Path
              d="M2.5 6h7M6.5 3l3 3-3 3"
              stroke={theme.accent}
              strokeWidth={1.6}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Svg>
        </View>
      </View>
    </Pressable>
  );
}
